import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixRestaurantsV1OperationGroup from './restaurants-v1-operation-group-operation-groups.http.js';
// @ts-ignore
import { transformSDKImageToRESTImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * An operation group is a aggregation of operations that each one of them point to a different location.
 * Each operation group has different ordering page.
 */
export interface OperationGroup {
  /**
   * OperationsGroup ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the OperationsGroup is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the OperationsGroup.
   *
   * Ignored when creating a OperationsGroup.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the OperationsGroup was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the OperationsGroup was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * The name of the operations group
   * @minLength 1
   * @maxLength 500
   */
  name?: string | null;
  /**
   * Cover image displayed on the ordering page in the Wix editor and on the published site.
   * @internal
   */
  coverImage?: string;
  /** Data Extensions */
  extendedFields?: ExtendedFields;
  /** Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of operation groups. */
  tags?: Tags;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 *
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, normally not given to site members or visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  tags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface CreateOperationGroupRequest {
  /** OperationGroup to be created. */
  operationGroup: OperationGroup;
  /**
   * The source of this request.
   * @internal
   * @maxLength 50
   */
  source?: string | null;
}

export interface CreateOperationGroupResponse {
  /** The created OperationGroup. */
  operationGroup?: OperationGroup;
}

export interface GetOperationGroupRequest {
  /**
   * ID of the OperationGroup to retrieve.
   * @format GUID
   */
  operationGroupId: string;
}

export interface GetOperationGroupResponse {
  /** The requested OperationGroup. */
  operationGroup?: OperationGroup;
}

export interface UpdateOperationGroupRequest {
  /** OperationGroup to be updated, may be partial. */
  operationGroup: OperationGroup;
  /**
   * Set of fields to update.
   *
   * Fields that aren't included in `fieldMask.paths` are ignored.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdateOperationGroupResponse {
  /** Updated OperationGroup. */
  operationGroup?: OperationGroup;
}

export interface DeleteOperationGroupRequest {
  /**
   * Id of the OperationGroup to delete.
   * @format GUID
   */
  operationGroupId: string;
}

export interface DeleteOperationGroupResponse {}

export interface QueryOperationGroupsRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryOperationGroupsResponse {
  /** List of OperationGroups. */
  operationGroups?: OperationGroup[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface BulkCreateOperationGroupsRequest {
  /**
   * List of OperationGroups to be created
   * @minSize 1
   * @maxSize 100
   */
  operationGroups: OperationGroup[];
  /** set to `true` if you wish to receive back the created OperationGroups in the response */
  returnEntity?: boolean;
}

export interface BulkCreateOperationGroupsResponse {
  /**
   * List of the bulk create operation results including the OperationGroups and metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkOperationGroupResult[];
  /** Metadata regarding the bulk create operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkOperationGroupResult {
  /** Metadata regarding the specific single create operation */
  itemMetadata?: ItemMetadata;
  /** Only exists if `returnEntity` was set to true in the request */
  item?: OperationGroup;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateOperationGroupsRequest {
  /**
   * List of OperationGroups to be updated.
   * @minSize 1
   * @maxSize 100
   */
  operationGroups: MaskedOperationGroup[];
  /** set to `true` if you wish to receive back the updated OperationGroups in the response */
  returnEntity?: boolean;
}

export interface MaskedOperationGroup {
  /** OperationGroup to be updated, may be partial */
  operationGroup?: OperationGroup;
  /**
   * Explicit list of fields to update.
   * @internal
   */
  fieldMask?: string[];
}

export interface BulkUpdateOperationGroupsResponse {
  /**
   * Results
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateOperationGroupsResponseBulkOperationGroupResult[];
  /** Metadata regarding the bulk update operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateOperationGroupsResponseBulkOperationGroupResult {
  /** Metadata regarding the specific single update operation */
  itemMetadata?: ItemMetadata;
  /** Only exists if `returnEntity` was set to true in the request */
  item?: OperationGroup;
}

export interface BulkDeleteOperationGroupsRequest {
  /**
   * OperationGroup ids to be deleted
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  operationGroupIds: string[];
}

export interface BulkDeleteOperationGroupsResponse {
  /**
   * Results
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkDeleteOperationGroupsResponseBulkOperationGroupResult[];
  /** Metadata regarding the bulk delete operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteOperationGroupsResponseBulkOperationGroupResult {
  /** Metadata regarding the specific single delete operation */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateOperationGroupTagsRequest {
  /**
   * IDs of the operation groups to update tags for.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  operationGroupIds: string[];
  /** Tags to assign to the operation groups. */
  assignTags?: Tags;
  /** Tags to unassign from the operation groups. */
  unassignTags?: Tags;
}

export interface BulkUpdateOperationGroupTagsResponse {
  /**
   * Results of the bulk update.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateOperationGroupTagsResult[];
  /** Metadata for the bulk update. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateOperationGroupTagsResult {
  /** Metadata for the updated operation group. */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateOperationGroupTagsByFilterRequest {
  /** Filter that determines which operation groups to update tags for. */
  filter: Record<string, any> | null;
  /** Tags to assign to the operation groups. */
  assignTags?: Tags;
  /** Tags to unassign from the operation groups. */
  unassignTags?: Tags;
}

export interface BulkUpdateOperationGroupTagsByFilterResponse {
  /**
   * Job ID. Pass this ID to Get Async Job ([SDK](https://dev.wix.com/docs/sdk/backend-modules/async-jobs/get-async-job) | [REST](https://dev.wix.com/docs/rest/business-management/async-job/get-async-job)) to track the job's status.
   * @format GUID
   */
  jobId?: string;
}

/** @docsIgnore */
export type BulkUpdateOperationGroupTagsApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdateOperationGroupTagsByFilterApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface OperationGroupCreatedEnvelope {
  entity: OperationGroup;
  metadata: EventMetadata;
}

/** @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.OPERATION_GROUP_READ
 * @webhook
 * @eventType wix.restaurants.v1.operation_group_created
 * @slug created
 * @documentationMaturity preview
 */
export declare function onOperationGroupCreated(
  handler: (event: OperationGroupCreatedEnvelope) => void | Promise<void>
): void;

export interface OperationGroupDeletedEnvelope {
  entity: OperationGroup;
  metadata: EventMetadata;
}

/** @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.OPERATION_GROUP_READ
 * @webhook
 * @eventType wix.restaurants.v1.operation_group_deleted
 * @slug deleted
 * @documentationMaturity preview
 */
export declare function onOperationGroupDeleted(
  handler: (event: OperationGroupDeletedEnvelope) => void | Promise<void>
): void;

export interface OperationGroupUpdatedEnvelope {
  entity: OperationGroup;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/** @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.OPERATION_GROUP_READ
 * @webhook
 * @eventType wix.restaurants.v1.operation_group_updated
 * @slug updated
 * @documentationMaturity preview
 */
export declare function onOperationGroupUpdated(
  handler: (event: OperationGroupUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a OperationGroup.
 * @param operationGroup - OperationGroup to be created.
 * @public
 * @requiredField operationGroup
 * @requiredField operationGroup.name
 * @permissionId RESTAURANTS.OPERATION_GROUP_CREATE
 * @applicableIdentity APP
 * @returns The created OperationGroup.
 * @fqn wix.restaurants.v1.OperationGroupService.CreateOperationGroup
 */
export async function createOperationGroup(
  operationGroup: NonNullablePaths<OperationGroup, `name`, 2>
): Promise<NonNullablePaths<OperationGroup, `tags.privateTags.tagIds`, 4>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ operationGroup: operationGroup }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'operationGroup.coverImage' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixRestaurantsV1OperationGroup.createOperationGroup(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'operationGroup.coverImage' }],
        },
      ])
    )?.operationGroup!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { operationGroup: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['operationGroup']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a OperationGroup.
 * @param operationGroupId - ID of the OperationGroup to retrieve.
 * @public
 * @requiredField operationGroupId
 * @permissionId RESTAURANTS.OPERATION_GROUP_READ
 * @applicableIdentity APP
 * @returns The requested OperationGroup.
 * @fqn wix.restaurants.v1.OperationGroupService.GetOperationGroup
 */
export async function getOperationGroup(
  operationGroupId: string
): Promise<NonNullablePaths<OperationGroup, `tags.privateTags.tagIds`, 4>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    operationGroupId: operationGroupId,
  });

  const reqOpts =
    ambassadorWixRestaurantsV1OperationGroup.getOperationGroup(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'operationGroup.coverImage' }],
        },
      ])
    )?.operationGroup!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { operationGroupId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['operationGroupId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a OperationGroup.
 *
 *
 * Each time the OperationGroup is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the OperationGroup.
 * This ensures you're working with the latest OperationGroup
 * and prevents unintended overwrites.
 * @param _id - OperationsGroup ID.
 * @public
 * @requiredField _id
 * @requiredField operationGroup
 * @requiredField operationGroup.revision
 * @permissionId RESTAURANTS.OPERATION_GROUP_UPDATE
 * @applicableIdentity APP
 * @returns Updated OperationGroup.
 * @fqn wix.restaurants.v1.OperationGroupService.UpdateOperationGroup
 */
export async function updateOperationGroup(
  _id: string,
  operationGroup: NonNullablePaths<UpdateOperationGroup, `revision`, 2>
): Promise<NonNullablePaths<OperationGroup, `tags.privateTags.tagIds`, 4>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      operationGroup: { ...operationGroup, id: _id },
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'operationGroup.coverImage' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixRestaurantsV1OperationGroup.updateOperationGroup(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'operationGroup.coverImage' }],
        },
      ])
    )?.operationGroup!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { operationGroup: '$[1]' },
        explicitPathsToArguments: { 'operationGroup.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'operationGroup']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateOperationGroup {
  /**
   * OperationsGroup ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the OperationsGroup is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the OperationsGroup.
   *
   * Ignored when creating a OperationsGroup.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the OperationsGroup was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the OperationsGroup was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * The name of the operations group
   * @minLength 1
   * @maxLength 500
   */
  name?: string | null;
  /**
   * Cover image displayed on the ordering page in the Wix editor and on the published site.
   * @internal
   */
  coverImage?: string;
  /** Data Extensions */
  extendedFields?: ExtendedFields;
  /** Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of operation groups. */
  tags?: Tags;
}

/**
 * Deletes a OperationGroup.
 *
 *
 * Deleting a OperationGroup permanently removes them from the OperationGroup List.
 * @param operationGroupId - Id of the OperationGroup to delete.
 * @public
 * @requiredField operationGroupId
 * @permissionId RESTAURANTS.OPERATION_GROUP_DELETE
 * @applicableIdentity APP
 * @fqn wix.restaurants.v1.OperationGroupService.DeleteOperationGroup
 */
export async function deleteOperationGroup(
  operationGroupId: string
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    operationGroupId: operationGroupId,
  });

  const reqOpts =
    ambassadorWixRestaurantsV1OperationGroup.deleteOperationGroup(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { operationGroupId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['operationGroupId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of OperationGroups.
 * @public
 * @permissionId RESTAURANTS.OPERATION_GROUP_READ
 * @applicableIdentity APP
 * @fqn wix.restaurants.v1.OperationGroupService.QueryOperationGroups
 */
export function queryOperationGroups(): OperationGroupsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    OperationGroup,
    'CURSOR',
    QueryOperationGroupsRequest,
    QueryOperationGroupsResponse
  >({
    func: async (payload: QueryOperationGroupsRequest) => {
      const reqOpts =
        ambassadorWixRestaurantsV1OperationGroup.queryOperationGroups(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryOperationGroupsRequest['query']) => {
      const args = [query, {}] as [QueryOperationGroupsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryOperationGroupsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTImageToSDKImage,
            paths: [{ path: 'operationGroups.coverImage' }],
          },
        ])
      );

      return {
        items: transformedData?.operationGroups,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface OperationGroupsQueryResult extends QueryCursorResult {
  items: OperationGroup[];
  query: OperationGroupsQueryBuilder;
  next: () => Promise<OperationGroupsQueryResult>;
  prev: () => Promise<OperationGroupsQueryResult>;
}

export interface OperationGroupsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => OperationGroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => OperationGroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => OperationGroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => OperationGroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => OperationGroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => OperationGroupsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName: '_id' | 'name',
    value: string
  ) => OperationGroupsQueryBuilder;
  in: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => OperationGroupsQueryBuilder;
  exists: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: boolean
  ) => OperationGroupsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<'_id' | '_createdDate' | '_updatedDate' | 'name'>
  ) => OperationGroupsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<'_id' | '_createdDate' | '_updatedDate' | 'name'>
  ) => OperationGroupsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => OperationGroupsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => OperationGroupsQueryBuilder;
  find: () => Promise<OperationGroupsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.restaurants.v1.OperationGroupService.QueryOperationGroups
 * @requiredField query
 */
export async function typedQueryOperationGroups(
  query: OperationGroupQuery
): Promise<
  NonNullablePaths<QueryOperationGroupsResponse, `operationGroups`, 2>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixRestaurantsV1OperationGroup.queryOperationGroups(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'operationGroups.coverImage' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface OperationGroupQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_createdDate', '_id', '_updatedDate', 'name'];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  OperationGroup,
  OperationGroupQuerySpec
>;
export type OperationGroupQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: /*#__PURE__*/ createQueryUtils<
    OperationGroup,
    OperationGroupQuerySpec,
    OperationGroupQuery
  >(),
};

/**
 * Create multiple OperationGroups in a single request. Works synchronously.
 * @param operationGroups - List of OperationGroups to be created
 * @public
 * @requiredField operationGroups
 * @requiredField operationGroups.name
 * @permissionId RESTAURANTS.OPERATION_GROUP_CREATE
 * @applicableIdentity APP
 * @fqn wix.restaurants.v1.OperationGroupService.BulkCreateOperationGroups
 */
export async function bulkCreateOperationGroups(
  operationGroups: NonNullablePaths<OperationGroup, `name`, 2>[],
  options?: BulkCreateOperationGroupsOptions
): Promise<
  NonNullablePaths<
    BulkCreateOperationGroupsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      operationGroups: operationGroups,
      returnEntity: options?.returnEntity,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'operationGroups.coverImage' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixRestaurantsV1OperationGroup.bulkCreateOperationGroups(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'results.item.coverImage' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          operationGroups: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['operationGroups', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateOperationGroupsOptions {
  /** set to `true` if you wish to receive back the created OperationGroups in the response */
  returnEntity?: boolean;
}

/**
 * Update multiple OperationGroups in a single request. Works synchronously.
 * @param operationGroups - List of OperationGroups to be updated.
 * @public
 * @requiredField operationGroups
 * @requiredField operationGroups.operationGroup.revision
 * @permissionId RESTAURANTS.OPERATION_GROUP_UPDATE
 * @applicableIdentity APP
 * @fqn wix.restaurants.v1.OperationGroupService.BulkUpdateOperationGroups
 */
export async function bulkUpdateOperationGroups(
  operationGroups: NonNullablePaths<
    MaskedOperationGroup,
    `operationGroup.revision`,
    3
  >[],
  options?: BulkUpdateOperationGroupsOptions
): Promise<
  NonNullablePaths<
    BulkUpdateOperationGroupsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      operationGroups: operationGroups,
      returnEntity: options?.returnEntity,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'operationGroups.operationGroup.coverImage' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixRestaurantsV1OperationGroup.bulkUpdateOperationGroups(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'results.item.coverImage' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          operationGroups: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['operationGroups', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateOperationGroupsOptions {
  /** set to `true` if you wish to receive back the updated OperationGroups in the response */
  returnEntity?: boolean;
}

/**
 * Delete multiple OperationGroups in a single request. Works synchronously.
 * @param operationGroupIds - OperationGroup ids to be deleted
 * @public
 * @requiredField operationGroupIds
 * @permissionId RESTAURANTS.OPERATION_GROUP_DELETE
 * @applicableIdentity APP
 * @fqn wix.restaurants.v1.OperationGroupService.BulkDeleteOperationGroups
 */
export async function bulkDeleteOperationGroups(
  operationGroupIds: string[]
): Promise<
  NonNullablePaths<
    BulkDeleteOperationGroupsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    operationGroupIds: operationGroupIds,
  });

  const reqOpts =
    ambassadorWixRestaurantsV1OperationGroup.bulkDeleteOperationGroups(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { operationGroupIds: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['operationGroupIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Synchronously update tags on multiple operation groups.
 * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
 * @param operationGroupIds - IDs of the operation groups to update tags for.
 * @public
 * @requiredField operationGroupIds
 * @permissionId RESTAURANTS.OPERATION_GROUP_UPDATE_TAGS
 * @applicableIdentity APP
 * @fqn wix.restaurants.v1.OperationGroupService.BulkUpdateOperationGroupTags
 */
export async function bulkUpdateOperationGroupTags(
  operationGroupIds: string[],
  options?: BulkUpdateOperationGroupTagsOptions
): Promise<
  NonNullablePaths<
    BulkUpdateOperationGroupTagsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkUpdateOperationGroupTagsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    operationGroupIds: operationGroupIds,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixRestaurantsV1OperationGroup.bulkUpdateOperationGroupTags(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          operationGroupIds: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['operationGroupIds', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateOperationGroupTagsOptions {
  /** Tags to assign to the operation groups. */
  assignTags?: Tags;
  /** Tags to unassign from the operation groups. */
  unassignTags?: Tags;
}

/**
 * Asynchronously update tags on multiple operation groups according to the specified filter.
 * If a filter isn't specified, this method updates all operation groups.
 * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
 * @param filter - Filter that determines which operation groups to update tags for.
 * @public
 * @requiredField filter
 * @permissionId RESTAURANTS.OPERATION_GROUP_UPDATE_TAGS
 * @applicableIdentity APP
 * @fqn wix.restaurants.v1.OperationGroupService.BulkUpdateOperationGroupTagsByFilter
 */
export async function bulkUpdateOperationGroupTagsByFilter(
  filter: Record<string, any>,
  options?: BulkUpdateOperationGroupTagsByFilterOptions
): Promise<
  NonNullablePaths<BulkUpdateOperationGroupTagsByFilterResponse, `jobId`, 2> & {
    __applicationErrorsType?: BulkUpdateOperationGroupTagsByFilterApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixRestaurantsV1OperationGroup.bulkUpdateOperationGroupTagsByFilter(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateOperationGroupTagsByFilterOptions {
  /** Tags to assign to the operation groups. */
  assignTags?: Tags;
  /** Tags to unassign from the operation groups. */
  unassignTags?: Tags;
}
